import React, { useEffect, useRef, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import JarvisOrb from "@/components/JarvisOrb";
import StageEffects from "@/components/StageEffects";
import StageVisual from "@/components/StageVisual";
import LoginGate from "@/components/LoginGate";
import Drop from "@/pages/Drop";
import Display from "@/pages/Display";
import Showcard from "@/pages/Showcard";
import Setup from "@/pages/Setup";
import Trading from "@/pages/Trading";
import Coach from "@/pages/Coach";
import SpendTracker from "@/pages/SpendTracker";
import ChatHistory from "@/pages/ChatHistory";
import BackgroundWidgets from "@/components/BackgroundWidgets";
import MiniOrbWidget from "@/components/MiniOrbWidget";
import OrbWaveform from "@/components/OrbWaveform";
import SpectrumBars from "@/components/SpectrumBars";
import MicBlockedPanel from "@/components/MicBlockedPanel";
import CommandRail from "@/components/CommandRail";
import StatusBar from "@/components/StatusBar";
import "@/components/StatusBar.css";
import UniversalCommandBar from "@/components/UniversalCommandBar";
import "@/components/UniversalCommandBar.css";
import FollowUpChips from "@/components/FollowUpChips";
import IdleScreensaver from "@/components/IdleScreensaver";
import RadioBadge from "@/components/RadioBadge";
import QuickActionsFAB from "@/components/QuickActionsFAB";
import ProofCanvas from "@/components/ProofCanvas";
import DoorbellPopup from "@/components/DoorbellPopup";
import PinnedTile from "@/components/PinnedTile";
import GalaxyBackground from "@/components/GalaxyBackground";
import ThemedBackground from "@/components/ThemedBackground";
import DraggableMainStage from "@/components/DraggableMainStage";
import InAppBrowser from "@/components/InAppBrowser";
import SwarmChat from "@/components/SwarmChat";
import OrbChatWidget from "@/components/OrbChatWidget";
import QuickWidgetLauncher from "@/components/QuickWidgetLauncher";
import AgentRemindersToast from "@/components/AgentRemindersToast";
import LayoutTemplatesModal from "@/components/LayoutTemplatesModal";
import EyesButton from "@/components/EyesButton";
import AlarmRinger from "@/components/AlarmRinger";
import AlarmsButton from "@/components/AlarmsButton";
import PageLinks from "@/components/PageLinks";
import TradingOrbButton from "@/components/TradingOrbButton";
import CoachOrbButton from "@/components/CoachOrbButton";
import AgentInbox from "@/components/AgentInbox";
import ArmLiveTradingButton from "@/components/ArmLiveTradingButton";
import VoiceAnnouncementsPoller from "@/components/VoiceAnnouncementsPoller";
import AgentsRoster from "@/components/AgentsRoster";
import TalkingHead from "@/components/TalkingHead";
import AgentFaceStage from "@/components/AgentFaceStage";
import MainRobotHead from "@/components/MainRobotHead";
import ShutdownOverlay from "@/components/ShutdownOverlay";
import "@/components/QuickActionsFAB.css";
import useJarvis from "@/hooks/useJarvis";
import useTheme from "@/hooks/useTheme";
import { getStatus, getToken, me, logout, api } from "@/lib/api";
import { shouldFireBoot, markBootFired, greetingForHour, playBootStinger, playWakeSongIfUploaded } from "@/lib/bootSequence";
// MicOff icon used inside MicBlockedPanel

// Action types that trigger a stage visual
const VISUAL_ACTIONS = new Set([
  "open_app", "get_analytics", "get_finance", "get_trades", "place_trade",
  "close_trade", "close_all_trades", "get_weather", "read_news", "ebay_search",
  "web_search", "show_image", "agent_activity", "live_eye", "camera_health", "weather_diagnose",
  "list_layouts", "load_layout", "save_layout",
  "get_revenue", "list_notes", "list_reminders", "list_watches",
  "daily_summary", "report_problem", "escalate_to_emergent", "list_voices", "set_voice", "monthly_report", "open_calendar", "open_year_heatmap", "show_architecture", "command_center", "clarify", "list_sessions", "recall_chats", "open_session", "show_commands", "morning_brief", "tell_story", "teach_something", "how_i_work", "my_journey", "agent_studio", "agent_blueprint", "social_composer", "open_youtube", "open_my_trader", "read_bbc_news", "set_weather_city", "universal", "raw_hub_dashboard", "capital_pnl", "overnight", "settings", "diagnostics", "search_flights", "directions", "find_recipe", "simulate_trade", "doorbell_latest", "youtube_recent", "toms_workshop", "toggle_plug", "generate_image", "ask_grok"
]);

function Console() {
  const [authed, setAuthed] = useState(!!getToken());
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    me().then(() => { setAuthed(true); setChecking(false); })
        .catch(() => { setAuthed(false); setChecking(false); });
    const handler = () => setAuthed(false);
    window.addEventListener("rawco-logout", handler);
    return () => window.removeEventListener("rawco-logout", handler);
  }, []);

  if (checking) return <div className="min-h-screen flex items-center justify-center"><div className="rc-label rc-blink">CHECKING DEVICE PAIRING</div></div>;
  if (!authed) return <LoginGate onSuccess={() => {
    // Flag this as a fresh sign-in so Stage can auto-fire the morning
    // brief regardless of the click interaction the user just made on
    // the PAIR button (which would otherwise block the auto-brief).
    try { sessionStorage.setItem("rawco_just_signed_in", String(Date.now())); } catch {}
    setAuthed(true);
  }} />;
  return <Stage />;
}

function Stage() {
  const [status, setStatus] = useState(null);
  const [visual, setVisual] = useState(null);
  const [swarmOpen, setSwarmOpen] = useState(true); // Owner asked: keep the chat always open
  const [swarmAutoMsg, setSwarmAutoMsg] = useState(null);
  const [layoutsOpen, setLayoutsOpen] = useState(false);
  // Hydrate eye-mute flag from localStorage so the off-switch persists
  // across page reloads. Owner asked "turn the eyes off" — once muted,
  // stays muted until they say "turn the eyes back on".
  useEffect(() => {
    try { window.__rcoEyesMuted = localStorage.getItem("rawco-eyes-muted") === "1"; }
    catch { window.__rcoEyesMuted = false; }
  }, []);
  const [inAppBrowser, setInAppBrowser] = useState(null);
  // Monotonic key so even if we set the SAME visual type twice (e.g. user asks
  // for system map after morning brief), React remounts StageVisual cleanly.
  const [visualKey, setVisualKey] = useState(0);
  // Pinned panels stay docked in the corner instead of being replaced. Each
  // entry: {type, action, reply, ts}. Right-click / long-press a panel to pin.
  const [pinned, setPinned] = useState([]); // legacy — kept for backwards compat but main no longer auto-pins
  const [lastReply, setLastReply] = useState("");
  const [suggestedReplies, setSuggestedReplies] = useState([]);
  const [now, setTime] = useState(new Date());
  const [orbStyle, setOrbStyle] = useState(() => localStorage.getItem("rawco_orb_style") || "constellation");
  const [lightningColor, setLightningColor] = useState(() => localStorage.getItem("rawco_lightning_color") || "gold");
  const [lightningTrigger, setLightningTrigger] = useState(0);
  const [panelRect, setPanelRect] = useState(null);
  // ─── PER-AGENT 3D FACE POP-OUT ────────────────────────────────────
  // Owner brief: "faces that come out the screen for each different agent
  // that's talking ... the mouth moves and head moves and make it look 3d
  // on the screen". When the brain emits talk_to_agent, we set
  // `activeAgentFace = {name, voiceId}`. The <AgentFaceStage> mounts and
  // performs the CSS-3D fly-in keyframes, lip-syncs to the audio, and
  // unmounts when speaking ends. Falls back to the regular orb otherwise.
  const [activeAgentFace, setActiveAgentFace] = useState(null);
  const [agentFaceLeaving, setAgentFaceLeaving] = useState(false);
  const agentFaceClearTimerRef = useRef(0);
  // 🩹 Owner bug: "blue robot head flashes before agent talks". Root cause
  // — pendingAgentFaceRef was a ref-only flag, so the brief window between
  // talk_to_agent firing and audio actually being ready let the
  // <MainRobotHead> fallback render. State mirror lets us suppress it.
  const [pendingAgentFace, setPendingAgentFace] = useState(false);
  // Pending agent face — stashed when talk_to_agent fires, only committed
  // to activeAgentFace when the audio element is actually ready to play
  // (so the dotted-mesh face appears AT the same moment the voice does,
  // not 30 seconds early sitting silent).
  const pendingAgentFaceRef = useRef(null);
  // ⏻ Goodnight overlay state — shown when the Siri "Good Night Raw
  // Company" shortcut fires the hardware_sleep webhook. The backend
  // already polls /api/jarvis/pending_shutdown for the Windows helper;
  // we piggy-back on that flag to put a visible 30s farewell on screen
  // so the dashboard doesn't just go black on the owner.
  const [showShutdown, setShowShutdown] = useState(false);
  useEffect(() => {
    let stopped = false;
    let lastCheck = 0;
    const poll = async () => {
      if (stopped || showShutdown) return;
      // 🚨 PERF Feb 2026: owner reported "app is running very slow". This
      // poll was hitting /jarvis/agent_reminders every 4 seconds — 21,600
      // requests per day per device. Bumped to 30s (still fast enough to
      // catch a sleep command within half a minute) and skip while tab
      // hidden so background tabs don't burn battery.
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await api.get("/jarvis/agent_reminders");
        if (r.data?.pending_shutdown) setShowShutdown(true);
      } catch { /* ignore */ }
    };
    const id = setInterval(poll, 30000);
    poll();
    return () => { stopped = true; clearInterval(id); };
  }, [showShutdown]);
  const themeHook = useTheme();
  const bcRef = useRef(null);
  const displayWinRef = useRef(null);
  const [mirrorOpen, setMirrorOpen] = useState(false);
  // Ref-mirror of mirrorOpen so the BroadcastChannel effect doesn't need it
  // as a dependency (was thrashing the BC + watchdog on every ping, freezing
  // the page during the morning brief 16-card fan-out).
  const mirrorOpenRef = useRef(false);
  useEffect(() => { mirrorOpenRef.current = mirrorOpen; }, [mirrorOpen]);

  // Auto-clear the active agent face when their voice finishes playing.
  // We watch jarvis.speaking — when it drops back to false while a face is
  // mounted, play the fade-out animation, then unmount 500ms later.
  // Note: this is intentionally NOT tied to a hard timeout — some agent
  // replies are long (>30s) and we want the face to stay until the audio
  // actually finishes.
  // jarvis is referenced via the hook below; the ref-grabbing useEffect at
  // the bottom of this file handles the actual subscription.
  // HARD CONTRACT: nothing renders on the orb screen. These buffers hold
  // visuals/cards while we wait for the mirror window to come online so we
  // never silently drop them and never fall back to the orb.
  const pendingProofRef = useRef([]);
  const [mirrorPromptVisible, setMirrorPromptVisible] = useState(false);

  // ─── PROOF-CARD FAN-OUT ──────────────────────────────────────────
  // ONLY fans out multi-window proof cards for the morning BRIEF (which
  // narrates many topics at once). Stand-alone commands like "show me my
  // P&L" should open ONE full-screen panel, not 5 floating cards — that
  // was the user's complaint. Each big panel still gets the full stage.
  const fanOutProofCards = (action) => {
    if (!action || !action.result) return;
    if (action.type !== "morning_brief") return; // hard gate
    const res = action.result;
    const cards = [];
    const t = action.type;

    if (t === "morning_brief") {
      const cap = res.capital || {};
      cards.push({ id: "brief-balance", kind: "balance", title: `CAPITAL · ${(cap.trade_mode || "").toUpperCase() || "OFFLINE"}`,
        accent: cap.trade_mode === "live" ? "mint" : "yellow",
        data: { balance: cap.balance_gbp, available: cap.balance_gbp, upl: cap.unrealised_pnl_gbp, account_id: cap.active_account_id, mode: cap.trade_mode } });
      if ((cap.positions || []).length) {
        cards.push({ id: "brief-positions", kind: "positions", title: "OPEN POSITIONS", accent: "cyan",
          data: { positions: cap.positions } });
      }
      // Wins / losses / all-time P&L — user complaint: "still doesn't have
      // my capital wins on there". This appears alongside CAPITAL · LIVE
      // and OPEN POSITIONS in the brief fan-out.
      const tr = res.trades || {};
      if ((tr.wins_total || 0) + (tr.losses_total || 0) > 0 || (tr.all_time_pnl_gbp || 0) !== 0) {
        cards.push({
          id: "brief-pnl",
          kind: "stat",
          title: "ALL-TIME P&L · WINS · LOSSES",
          accent: (tr.all_time_pnl_gbp || 0) >= 0 ? "mint" : "coral",
          data: {
            value: `£${Number(tr.all_time_pnl_gbp || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            label: `${tr.wins_total || 0}W · ${tr.losses_total || 0}L · ${tr.win_rate_pct || 0}% wr`,
            detail: `${tr.closed_24h || 0} closed in 24h · P&L £${Number(tr.closed_pnl_gbp || 0).toFixed(2)}`,
          },
        });
      }
      const msgs = res.messages || {};
      if ((msgs.unread || 0) > 0) {
        cards.push({ id: "brief-messages", kind: "messages", title: "UNREAD MESSAGES", accent: "magenta",
          data: { unread: msgs.unread, items: msgs.items || [] } });
      }
      const fin = res.finance || {};
      if (fin.entries_24h || fin.net_24h_gbp) {
        cards.push({ id: "brief-finance", kind: "finance", title: "FINANCE · LAST 24H", accent: "gold",
          data: { net: fin.net_24h_gbp, entries: fin.entries_24h, income: fin.income_today_gbp, outgoing: fin.outgoing_today_gbp } });
      }
      const rems = res.reminders_today || [];
      if (Array.isArray(rems) && rems.length) {
        cards.push({ id: "brief-reminders", kind: "reminders", title: "REMINDERS TODAY", accent: "coral", data: { items: rems } });
      }
      const iss = res.issues || {};
      if ((iss.needs_owner || 0) + (iss.auto_open || 0) > 0) {
        cards.push({ id: "brief-issues", kind: "issues", title: "OPEN ISSUES", accent: "purple", data: iss });
      }
      // NEW: Raw Hub dashboard summary — user asked to see all dashboards
      // in the brief fan-out (not just Capital).
      const hub = res.raw_hub || {};
      if ((hub.apps || []).length || (hub.total_revenue_gbp || 0) > 0) {
        cards.push({
          id: "brief-hub", kind: "hub", title: "RAW HUB · ALL APPS", accent: "purple",
          data: {
            total: hub.total_revenue_gbp, users: hub.total_users,
            apps: hub.apps || [],
          },
        });
      }
      // NEW: BBC News headlines — user asked for the latest news to be
      // visible on the mirror during the brief.
      const news = res.bbc_news || [];
      if (news.length) {
        cards.push({
          id: "brief-news", kind: "news", title: "BBC NEWS · TOP", accent: "coral",
          data: { items: news.slice(0, 4) },
        });
      }
      // ─── Quick lookup of per-app stats from the Raw Hub roll-up so the
      // launcher tiles below can show real revenue/users/views/subs.
      // Owner repeatedly asked: "it didn't tell me how many views or
      // subscribers or revenue about any of my apps".
      const appStatsByKey = {};
      for (const a of (hub.apps || [])) {
        const k = (a.app_key || a.key || a.name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        if (k) appStatsByKey[k] = a;
      }
      const stats = (key) => appStatsByKey[key] || null;
      // OWNER-REQUESTED (many times): Morning brief MUST include the actual
      // RAW-DATA + SnapDog app tiles on the mirror so he can see his
      // sister apps at a glance and tap straight into them. These render as
      // tappable branded launch tiles via ProofCanvas → `kind: "applaunch"`.
      cards.push({
        id: "brief-rawcompany", kind: "applaunch", title: "RAW-DATA", accent: "gold",
        data: { url: "https://rawcompany.uk", appName: "RAW-DATA", glyph: "★", brandKey: "rawcompany", stats: stats("rawcompany") },
      });
      cards.push({
        id: "brief-snapdog", kind: "applaunch", title: "SNAPDOG", accent: "magenta",
        data: { url: "https://dogsnap.co.uk", appName: "SnapDog", glyph: "🐕", brandKey: "snapdog", stats: stats("snapdog") },
      });
      cards.push({
        id: "brief-clothing", kind: "applaunch", title: "CLOTHING", accent: "cyan",
        data: { url: "https://rawcompanyclothing.uk", appName: "Clothing", glyph: "👕", brandKey: "clothing", stats: stats("clothing") },
      });

      // Owner asked: "missing Racing Form Guide and Raw Mixes." Adding
      // launcher tiles so all the sister apps show up on the brief grid.
      cards.push({
        id: "brief-rawmixes", kind: "applaunch", title: "RAW MIXES", accent: "orange",
        data: { url: "https://rawmixes.co.uk", appName: "Raw Mixes", glyph: "🎵", brandKey: "rawmixes", stats: stats("rawmixes") || stats("mixes") },
      });
      cards.push({
        id: "brief-racing", kind: "applaunch", title: "RACING FORM GUIDE", accent: "mint",
        data: { url: "https://rawcompanyracing.uk", appName: "Racing Form Guide", glyph: "🏇", brandKey: "racing", stats: stats("racing") || stats("racingformguide") },
      });
      cards.push({
        id: "brief-trader", kind: "applaunch", title: "RAW CO. TRADING", accent: "red",
        data: { url: "https://rawcotrading.uk", appName: "Raw Co. Trading", glyph: "📊", brandKey: "trader", stats: stats("trader") || stats("rawcotrading") },
      });
      // 7th app — RAW-DATA Trading platform (separate from Raw Co. Trading).
      // Owner explicitly counted "all 7 widgets" — this completes the set.
      cards.push({
        id: "brief-rctrading", kind: "applaunch", title: "RAW-DATA TRADING", accent: "gold-bright",
        data: { url: "https://rawcompanytrading.uk", appName: "RAW-DATA Trading", glyph: "💹", brandKey: "rctrading", stats: stats("rctrading") || stats("rawcompanytrading") },
      });

      // 🌤 WEATHER tile — ALWAYS push (owner: "the weather widget didn't
      // pop up but everything else did"). If the fetch failed we still
      // show the card with a "—" placeholder so the slot isn't missing.
      const wx = res.weather || {};
      cards.push({
        id: "brief-weather", kind: "weather",
        title: `WEATHER · ${(wx.location || "LONDON").toUpperCase()}`,
        accent: "cyan",
        data: {
          temp_c: typeof wx.temp_c === "number" ? wx.temp_c : null,
          code: wx.code || null,
          wind: wx.wind || null,
          location: wx.location || "London",
        },
      });

      // 🗓 CALENDAR tile — visual day-card showing today's events. This is
      // distinct from the "REMINDERS TODAY" card (which is for free-form
      // reminders the owner created via voice). ALWAYS shown in the brief
      // even if there are no events today — owner asked "where is my
      // calendar at?" so the tile must be visible every morning.
      cards.push({
        id: "brief-calendar", kind: "calendar",
        title: `CALENDAR · ${new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" }).toUpperCase()}`,
        accent: "yellow",
        data: { items: Array.isArray(rems) ? rems.slice(0, 6) : [], date: new Date().toISOString() },
      });

      // ─── HUB DASHBOARD CARDS — owner's #1 request: MIRROR the actual
      // cards from his RAW-DATA dashboard onto the screen, one card
      // per metric, big number visible, so he sees the same numbers he
      // pays his hub to surface. Each card is a `hub_tile` rendered with
      // the same big-£ / neon-accent style as rawcompany.uk/admin.
      // Zero-value tiles are skipped — owner: "I refuse to keep hearing
      // zero pounds, zero users, zero anything".
      const hf = res.hub_full || {};
      const sb = hf.stripe_balance || {};
      const ss = hf.stripe_summary || {};
      const ex = hf.expenses || {};
      const mp = hf.monthly_pnl || {};
      const at = hf.all_time || {};
      const dl = hf.downloads || {};
      const cr = hf.click_revenue || {};

      const num = (v) => (typeof v === "number" && !isNaN(v) ? v : 0);
      const pushHubTile = (id, title, accent, value, sub, deep) => {
        if (!num(value)) return;  // skip zero-value tiles
        cards.push({
          id: `brief-hub-${id}`, kind: "hub_tile", title, accent,
          data: {
            value: `£${num(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            sub: sub || "",
            deep: deep || null,
          },
        });
      };
      // Top-row tiles from rawcompany.uk/admin (Stripe in, Refunds, Available, Donations, Apps, Costs/mo)
      pushHubTile("stripe-in", "STRIPE IN", "mint",
        ss.stripe_in_gbp ?? ss.gross_gbp ?? ss.total_in,
        "all-time");
      pushHubTile("refunds", "REFUNDS", "coral",
        ss.refunds_gbp ?? ss.total_refunds,
        "all-time");
      pushHubTile("available", "AVAILABLE", "gold",
        sb.available_gbp ?? sb.available,
        "ready to payout");
      pushHubTile("donations", "DONATIONS", "magenta",
        ss.donations_gbp ?? ss.total_donations,
        "all-time");
      pushHubTile("apps-earnings", "APPS", "cyan",
        dl.total_earnings_gbp ?? dl.total_earnings,
        `${num(dl.total_downloads ?? dl.total)} downloads`);
      pushHubTile("costs-mo", "COSTS / MO", "coral",
        ex.total_monthly_gbp ?? ex.monthly,
        `£${num(ex.total_yearly_gbp ?? ex.yearly).toFixed(0)}/yr`);
      // Monthly P&L tile
      const net = num(mp.net_gbp ?? ((mp.revenue_gbp || 0) - (mp.costs_gbp || 0)));
      if (mp && (mp.revenue_gbp || mp.costs_gbp)) {
        cards.push({
          id: "brief-hub-pnl-month", kind: "hub_tile",
          title: "THIS MONTH P&L", accent: net >= 0 ? "mint" : "coral",
          data: {
            value: `${net >= 0 ? "+" : "−"}£${Math.abs(net).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            sub: `rev £${num(mp.revenue_gbp).toFixed(0)} · cost £${num(mp.costs_gbp).toFixed(0)}`,
            deep: null,
          },
        });
      }
      // All-time profit tile
      const allProfit = num(at.profit_gbp ?? at.profit);
      if (at && (at.income_gbp || at.expenses_gbp || allProfit)) {
        cards.push({
          id: "brief-hub-pnl-all", kind: "hub_tile",
          title: "ALL-TIME PROFIT", accent: allProfit >= 0 ? "gold-bright" : "coral",
          data: {
            value: `${allProfit >= 0 ? "+" : "−"}£${Math.abs(allProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            sub: `in £${num(at.income_gbp).toFixed(0)} · out £${num(at.expenses_gbp).toFixed(0)}`,
            deep: null,
          },
        });
      }
      // Affiliate clicks tile (Capital.com referral)
      const clicksAll = num(cr.all_time);
      if (clicksAll) {
        cards.push({
          id: "brief-hub-clicks", kind: "hub_tile",
          title: "AFFILIATE CLICKS", accent: "yellow",
          data: {
            value: `${clicksAll}`,
            sub: `today ${num(cr.today)} · week ${num(cr.this_week)}`,
            deep: null,
          },
        });
      }

      // 📈 TRADING PLATFORM launcher tile — taps straight to Capital.com so
      // the owner can drill into the actual broker UI alongside the in-app
      // balance card.
      cards.push({
        id: "brief-capital-launcher", kind: "applaunch", title: "TRADING · CAPITAL.COM", accent: "mint",
        data: { url: "https://capital.com/trading/platform", appName: "Capital.com", glyph: "📈", brandKey: "capital" },
      });
    } else if (t === "capital_pnl") {
      cards.push({ id: "cap-balance", kind: "balance", title: "ACCOUNT BALANCE", accent: "mint",
        data: { balance: (res.account?.balance?.balance || res.account?.balance), available: res.account?.balance?.available, upl: res.open_upl_gbp, account_id: res.account?.accountId, mode: res.trade_mode } });
      if ((res.open_positions || []).length) {
        cards.push({ id: "cap-positions", kind: "positions", title: "OPEN POSITIONS", accent: "cyan",
          data: { positions: (res.open_positions || []).map(p => ({
            symbol: (p.market?.instrumentName || p.market?.epic || p.symbol),
            direction: (p.position?.direction || p.direction),
            upl: (p.position?.upl ?? p.upl),
          })) } });
      }
      cards.push({ id: "cap-pnl", kind: "stat", title: "ALL-TIME P&L", accent: Number(res.total_pnl_gbp) >= 0 ? "mint" : "coral",
        data: { value: `£${Number(res.total_pnl_gbp || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, label: `${res.wins || 0}W · ${res.losses || 0}L · ${res.win_rate_pct || 0}% wr`, detail: `Today £${Number(res.today_pnl_gbp || 0).toFixed(2)}` } });
    } else if (t === "my_journey") {
      cards.push({ id: "j-apps", kind: "stat", title: "APPS BUILT", accent: "gold", data: { value: res.apps_built, label: `+${res.registered_external_apps || 0} registered`, detail: "" } });
      cards.push({ id: "j-keys", kind: "stat", title: "API KEYS", accent: "magenta", data: { value: res.api_keys_total, label: `${res.services_armed}/${res.services_total} services armed`, detail: "" } });
      cards.push({ id: "j-webhooks", kind: "stat", title: "WEBHOOKS", accent: "cyan", data: { value: res.webhooks_total, label: "real-time triggers", detail: "" } });
      cards.push({ id: "j-spend", kind: "spend", title: "EMERGENT SPEND", accent: "coral", data: { spend_gbp: res.spend_gbp, transactions: "" } });
      cards.push({ id: "j-hours", kind: "stat", title: "HOURS OF GRAFT", accent: "gold-bright", data: { value: `${res.hours_estimate}`, label: "conservative estimate", detail: "" } });
    } else if (t === "show_image") {
      // Owner asked: "show me a diagram of a Porsche / picture of X" — fans
      // out a single 'imagegallery' card with the images Tavily found.
      const imgs = Array.isArray(res.images) ? res.images.filter(i => i?.url) : [];
      if (imgs.length) {
        cards.push({
          id: `imggal-${Date.now()}`, kind: "imagegallery",
          title: (res.query || "IMAGES").toUpperCase(), accent: "gold-bright",
          data: { images: imgs, query: res.query },
        });
      }
    } else if (t === "raw_hub_dashboard") {
      cards.push({ id: "hub-total", kind: "stat", title: "RAW HUB", accent: "purple",
        data: { value: `£${Number(res.total_revenue_gbp || 0).toLocaleString()}`, label: `${(res.apps || []).length} apps reporting`, detail: `${res.total_users || 0} total users` } });
    }

    if (!cards.length) return;
    // 🛡 Owner Feb 2026: "the brief works now but half of the cards pop
    // up like 5 minutes later." Root cause: pendingProofRef was being
    // pushed EVERY card unconditionally — even when the mirror was
    // already receiving them via BroadcastChannel — so the queue built
    // up indefinitely. Any subsequent mirror re-mount / re-focus /
    // reconnect triggered a `request_state` → flushPending drained the
    // ENTIRE queue, replaying old cards minutes later.
    // Fix: (1) wipe the queue at the start of every new fanout so a
    // fresh brief supersedes any leftovers, and (2) only push cards to
    // the queue when the mirror isn't actively receiving them.
    pendingProofRef.current = [];
    // STRICT GUARD: BC ping is single source of truth for "mirror alive".
    // If we've heard from any mirror in the last 12s, don't open another.
    const recentPing = lastMirrorPingRef.current &&
                       (Date.now() - lastMirrorPingRef.current < 12000);
    const mirrorLive = !!mirrorOpen || recentPing;
    if (!recentPing && !mirrorOpen && (!displayWinRef.current || displayWinRef.current.closed)) {
      try {
        const url = `${window.location.origin}/display`;
        const win = window.open(url, "rawco-mirror", "width=1280,height=800,resizable=yes");
        if (win && !win.closed) displayWinRef.current = win;
        else setMirrorPromptVisible(true);
      } catch { setMirrorPromptVisible(true); }
    }

    // Fan all cards out fast (every 350ms) — the mirror grid now uses
    // tight 24px margins (mode="mirror" in ProofCanvas) so 13+ cards
    // spread evenly across the 1280px window instead of stacking.
    // Sequential narration-synced reveal was removed: too much
    // BroadcastChannel + setCards churn was making the page
    // unresponsive AND the cards lagged the actual spoken brief.
    // 80ms head-start so the proof_clear above lands first.
    cards.forEach((c, i) => {
      setTimeout(() => {
        try { bcRef.current?.postMessage({ type: "proof_open", card: c }); } catch { /* ignore */ }
        // Only buffer if the mirror wasn't alive at fanout-start AND still
        // isn't alive now (checked lazily to catch a mid-fanout reconnect).
        // If it IS alive, the BC.postMessage above delivered the card
        // directly and buffering it would only cause a duplicate on the
        // next request_state.
        const stillLive = mirrorLive ||
          (lastMirrorPingRef.current && Date.now() - lastMirrorPingRef.current < 12000);
        if (!stillLive) pendingProofRef.current.push(c);
      }, 80 + i * 350);
    });
  };
  // HARD CONTRACT (user-imposed):
  //   NO cards or windows EVER render on the orb screen.
  //   Everything goes to the mirror window. If the mirror isn't open, we
  //   open it and queue the visual until it acknowledges. If the popup is
  //   blocked, we surface a clear button — we do NOT fall back to orb.
  const pendingVisualsRef = useRef([]);
  const showVisual = (v) => {
    if (!v) return;
    setLightningTrigger(t => t + 1); // orb still flashes (no card, just lightning)
    if (mirrorOpen) {
      // Mirror IS open — push directly via BC. Orb stays clean.
      // 🚫 HARD CONTRACT (owner-imposed, restored Feb 2026 after I broke
      // it momentarily): NO cards EVER render on the orb screen. The
      // earlier "safety net for morning_brief on both screens" was
      // wrong — owner has spent £££ to keep the orb clean. The mirror
      // is the only place visuals belong when it's open.
      if (v?.action?.type && v.action.type !== "morning_brief") {
        bcRef.current?.postMessage({ type: "clear" });
        bcRef.current?.postMessage({ type: "clear_proof" });
      }
      bcRef.current?.postMessage({ type: "visual", visual: v });
      try { displayWinRef.current?.focus(); } catch {}
      return;
    }
    // 🚨 Mirror NOT open — owner complaint: "show me the system map / show me
    // a Ferrari / show the architecture — NOTHING renders". Root cause: the
    // visual was only ever pushed to the mirror via BroadcastChannel. If the
    // mirror window wasn't open (popup blocked, user only on orb screen),
    // setVisual was NEVER called and the orb stayed empty. We now ALWAYS
    // render locally on the orb as a fallback, AND still try to pop the
    // mirror in the background. Either way the owner sees the card.
    setVisual(v);
    setVisualKey(k => k + 1);

    pendingVisualsRef.current.push(v);
    const recentPing = lastMirrorPingRef.current &&
                       (Date.now() - lastMirrorPingRef.current < 12000);
    if (recentPing) return;  // mirror alive somewhere, BC will deliver
    if (displayWinRef.current && !displayWinRef.current.closed) return;
    try {
      const url = `${window.location.origin}/display`;
      const win = window.open(url, "rawco-mirror", "width=1280,height=800,resizable=yes");
      if (win && !win.closed) {
        displayWinRef.current = win;
        return;
      }
    } catch {}
    setMirrorPromptVisible(true);
  };

  // Fire lightning every time a new local panel appears — bolt is biased
  // towards the panel's rect so it visibly "summons" the content.
  useEffect(() => {
    if (visual) {
      // Defer one frame so the panel actually has bounds
      requestAnimationFrame(() => {
        try {
          const el = document.querySelector(".stage-visual > *");
          if (el) {
            const r = el.getBoundingClientRect();
            setPanelRect({ left: r.left, top: r.top, width: r.width, height: r.height });
          }
        } catch {}
        setLightningTrigger(t => t + 1);
        bcRef.current?.postMessage({ type: "lightning_strike" });
      });
    } else {
      setPanelRect(null);
    }
  }, [visual]);

  // BroadcastChannel — push state to the secondary screen
  // Mirror window pings every 4s. We track the timestamp of the last ping
  // so we can flip mirrorOpen back to false if the mirror tab is closed
  // (otherwise cards would be silently sent to BC with nobody listening).
  const lastMirrorPingRef = useRef(0);
  // Dedupe ref scoped specifically to mirror-side brief requests. Kept
  // separate from window.__rcoLastBriefAt (which is the 5-minute lock
  // owned by useJarvis.sendToBrain) so we don't accidentally trip that
  // lock and silently swallow the brief — that was the "brief never
  // renders on mirror" bug.
  const lastMirrorBriefReqRef = useRef(0);
  // Same idea for the boot autoBrief 30s double-mount guard.
  const autoBriefLastAtRef = useRef(0);
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const bc = new BroadcastChannel("rawco-stage");
    bcRef.current = bc;
    const flushPending = () => {
      // Drain any buffered proof cards now that the mirror is alive.
      // 🛡 Owner Feb 2026 fix: cap the drain at 40 cards. If the buffer
      // has more, something went sideways — dumping a hundred stale
      // cards on a re-connect gave the "half the brief pops up 5 min
      // later" bug. Keep only the most recent 40 (a full brief has
      // ~24 cards).
      if (pendingProofRef.current.length) {
        const recent = pendingProofRef.current.slice(-40);
        recent.forEach((c, i) => {
          setTimeout(() => bc.postMessage({ type: "proof_open", card: c }), i * 200);
        });
        pendingProofRef.current = [];
      }
      // Drain queued full visuals.
      if (pendingVisualsRef.current?.length) {
        pendingVisualsRef.current.forEach((v) => bc.postMessage({ type: "visual", visual: v }));
        pendingVisualsRef.current = [];
      }
      setMirrorPromptVisible(false);
    };
    bc.onmessage = (e) => {
      const msg = e.data || {};
      if (msg.type === "request_state") {
        // Mirror just opened. Send theme; flush any queued cards/visuals.
        bc.postMessage({ type: "theme", orb_style: orbStyle, lightning_color: lightningColor });
        lastMirrorPingRef.current = Date.now();
        setMirrorOpen(true);
        flushPending();
      } else if (msg.type === "ping") {
        bc.postMessage({ type: "pong" });
        lastMirrorPingRef.current = Date.now();
        if (!mirrorOpenRef.current) {
          setMirrorOpen(true);
          flushPending();
        }
      } else if (msg.type === "command" && msg.text) {
        // Mirror sent us a command (owner clicked an interactive element
        // over there — e.g. an agent pill in the architecture diagram).
        // Route it through the brain like a normal voice/typed command.
        try { jarvis.sendText?.(msg.text); } catch { /* ignore */ }
      } else if (msg.type === "request_brief") {
        // ⚡ Owner Feb 2026: "mirror doesn't show brief automatically".
        // Mirror connection ALWAYS triggers a fresh brief — bypassing
        // the orb-side boot guards (visualRef, userInteracted, 30s lock,
        // jarvis.speaking). De-dup: only one brief per 10s so a flaky
        // mirror reconnect doesn't spam.
        //
        // 🛡 IMPORTANT: do NOT touch __rcoLastBriefAt here — that flag
        // is owned by useJarvis.sendToBrain (5-minute lock). If we set
        // it before calling sendText the hook sees a fresh lock and
        // silently aborts ("brief lock active"), which is exactly the
        // bug the owner reported: mirror connects, lock fires, brief
        // never plays. We use a separate ref scoped to mirror-pings.
        const lastMirrorReq = Number(lastMirrorBriefReqRef.current || 0);
        if (Date.now() - lastMirrorReq > 10000) {
          lastMirrorBriefReqRef.current = Date.now();
          try { jarvis.sendText?.("give me my morning brief", "morning_brief", { silentOnError: true }); } catch { /* ignore */ }
        }
      }
    };
    // Watchdog: if no ping for 10s, mirror is dead. We flip the flag so the
    // NEXT showVisual() call auto-pops the mirror again. We do NOT fall back
    // to the orb screen — user contract is "never on orb".
    const watchdog = setInterval(() => {
      if (mirrorOpenRef.current && lastMirrorPingRef.current &&
          Date.now() - lastMirrorPingRef.current > 10000) {
        setMirrorOpen(false);
      }
    }, 3000);
    return () => { bc.close(); clearInterval(watchdog); };
  }, [orbStyle, lightningColor]);

  // Refs to give the BC handler access to latest state without recreating the listener
  const visualRef = useRef(null);
  const lastReplyRef = useRef("");
  // Track whether the owner has manually interacted since boot. If true, the
  // 8s auto morning-brief is aborted so it can't clobber their own command.
  const userInteractedRef = useRef(false);
  // Persistent reference to the in-app live radio <audio> element. Allows
  // "stop_audio" to silence the station the owner is listening to without
  // navigating away.
  const radioRef = useRef(null);
  const [radioPlaying, setRadioPlaying] = useState(null);
  useEffect(() => { visualRef.current = visual; bcRef.current?.postMessage(visual ? { type: "visual", visual } : { type: "clear" }); }, [visual]);

  useEffect(() => { lastReplyRef.current = lastReply; if (lastReply) bcRef.current?.postMessage({ type: "reply", text: lastReply }); }, [lastReply]);

  // Cross-component swarm trigger: any panel (e.g. morning brief, error toast)
  // can dispatch `rawco-open-swarm` with { detail: { message } } and the chat
  // pops with that question auto-fired.
  useEffect(() => {
    const handler = (e) => {
      const msg = e?.detail?.message || "";
      setSwarmAutoMsg(msg);
      setSwarmOpen(true);
    };
    window.addEventListener("rawco-open-swarm", handler);
    return () => window.removeEventListener("rawco-open-swarm", handler);
  }, []);

  const popOutMirror = () => {
    // Re-focus existing window if still open
    if (displayWinRef.current && !displayWinRef.current.closed) {
      displayWinRef.current.focus();
      return;
    }
    // 🛡 Debounce racing calls. `getScreenDetails().then(open)` is async,
    // so back-to-back "open the mirror" hits (from voice + BroadcastChannel
    // + manual click) can all pass the guard above BEFORE displayWinRef
    // is set → multiple popups. Track an in-progress flag synchronously.
    if (window.__rcoMirrorOpening) {
      console.warn("[mirror] open already in progress — ignoring duplicate");
      return;
    }
    window.__rcoMirrorOpening = true;
    // Clear the flag either when the window opens or after 3s (whichever first)
    const clearFlag = () => { window.__rcoMirrorOpening = false; };
    setTimeout(clearFlag, 3000);
    localStorage.setItem("rawco_mirror_pref", "on");
    const open = (left, top, width, height) => {
      const features = `popup=yes,noopener=no,left=${left},top=${top},width=${width},height=${height}`;
      displayWinRef.current = window.open("/display", "rawco-display", features);
      if (displayWinRef.current) setMirrorOpen(true);
      clearFlag();
    };
    if (window.getScreenDetails) {
      window.getScreenDetails().then((sd) => {
        const primary = sd.currentScreen;
        const other = sd.screens.find(s => s !== primary) || primary;
        open(other.availLeft, other.availTop, other.availWidth, other.availHeight);
      }).catch(() => open(window.screenX + window.outerWidth, window.screenY, 1280, 800));
    } else {
      open(window.screenX + window.outerWidth, window.screenY, 1280, 800);
    }
  };

  const handleAction = (action, reply = null) => {
    if (!action) return;
    // 👁 EYE MUTE — voice command "turn the eyes off" returns this. Sets
    // a window flag the EyesButton keep-watching poller checks before
    // narrating. Owner: "eyes won't stop describing things, talks over me".
    if (action.type === "set_eye_mute") {
      try { window.__rcoEyesMuted = !!action.muted; } catch { /* ignore */ }
      try { localStorage.setItem("rawco-eyes-muted", action.muted ? "1" : "0"); } catch { /* ignore */ }
      return;
    }
    // 👁 Single-tile spawn — e.g. "show me the eye" emits a show_proof_card
    // action with a single tile. Push it onto the same BroadcastChannel
    // queue that morning brief uses so it lands on the mirror.
    if (action.type === "show_proof_card" && action.card) {
      const c = { ...action.card, spawnedAt: Date.now() };
      try { bcRef.current?.postMessage({ type: "proof_open", card: c }); } catch { /* ignore */ }
      // If mirror isn't open, queue and pop it
      if (!mirrorOpenRef.current) {
        pendingProofRef.current.push(c);
        showVisual({ type: "mirror_pop" });
      }
      return;
    }

    // ─── PROOF CARD FAN-OUT ──────────────────────────────────────────
    // For multi-stat actions (morning_brief, capital_pnl, my_journey,
    // raw_hub_dashboard), spawn a sequence of draggable proof-cards on
    // the canvas as the brain narrates. User can close or drag any.
    fanOutProofCards(action);
    // (Note: SwarmChat starts open=true by default, so no extra trigger
    // needed for morning_brief — see useState(true) on swarmOpen.)

    // 💬 Talk-to-specific-agent — owner asked: "if I say 'hey Brian',
    // Brian talks back; if I say 'hey Bella I need help', she replies in
    // HER own voice." The brain's talk_to_agent action returns a `reply`
    // text + a `voice_id`; we cancel the default Jarvis voice and speak
    // the reply in the matched agent's ElevenLabs voice instead. We ALSO
    // append the user's request + the agent's reply to the SwarmChat
    // thread so the owner sees the exchange in the chat panel (owner
    // complaint: "I asked Tom about the screen freeze but he didn't come
    // up in the swarm chat").
    if (action.type === "talk_to_agent" && action.result?.reply) {
      const r = action.result;
      // 🎭 30-SECOND-DELAY FIX — previously the agent face was mounted
      // IMMEDIATELY when the talk_to_agent action returned, but the
      // ElevenLabs TTS roundtrip + audio download for a new voice can
      // take 5-30 seconds (cold start). The face appeared and just sat
      // there silent. Now we stash the pending agent and only commit
      // the face when the next audioEl change comes through (which
      // means audio is actually ready to play). useEffect below watches
      // for the audioEl flip.
      pendingAgentFaceRef.current = { name: r.agent_name || "Agent", voiceId: r.voice_id || "" };
      setPendingAgentFace(true);
      // Speak the agent's reply in their own voice
      jarvis.speak?.(r.reply, r.voice_id || "").catch(() => {});
      // Broadcast to SwarmChat so the panel shows the message thread.
      try {
        const userMsg = (action.payload && action.payload.message) || r.user_message || "";
        window.dispatchEvent(new CustomEvent("rawco-swarm-append", {
          detail: {
            agentName: r.agent_name,
            agentReply: r.reply,
            userMessage: userMsg,
          },
        }));
        window.dispatchEvent(new Event("rawco-open-swarm"));
      } catch { /* ignore */ }
      return;
    }
    if (action.type === "set_theme") {
      // Re-fetch theme from server (action.result IS the new theme but easier to just refresh)
      themeHook.refresh().then(t => {
        if (t?.orb_style) setOrbStyle(t.orb_style);
        if (t?.lightning_color) setLightningColor(t.lightning_color);
      });
      bcRef.current?.postMessage({ type: "theme-changed" });
      return;
    }
    if (action.type === "add_widget" || action.type === "remove_widget" || action.type === "clear_widgets") {
      window.dispatchEvent(new Event("rawco-widgets-changed"));
      bcRef.current?.postMessage({ type: "widgets-changed" });
      return;
    }
    if (action.type === "set_orb_style" && action.result?.style) {
      const s = action.result.style;
      localStorage.setItem("rawco_orb_style", s);
      setOrbStyle(s);
      bcRef.current?.postMessage({ type: "theme", orb_style: s });
      return;
    }
    if (action.type === "set_lightning_color" && action.result?.color) {
      const c = action.result.color;
      localStorage.setItem("rawco_lightning_color", c);
      setLightningColor(c);
      window.dispatchEvent(new Event("rawco-lightning-color"));
      bcRef.current?.postMessage({ type: "theme", lightning_color: c });
      return;
    }
    if (action.type === "open_display") {
      popOutMirror();
      return;
    }
    if (action.type === "close_display") {
      try { displayWinRef.current?.close(); } catch {}
      displayWinRef.current = null;
      setMirrorOpen(false);
      localStorage.setItem("rawco_mirror_pref", "off");
      return;
    }
    if ((action.type === "open_url" || action.type === "bank_transfer") && action.result?.url) {
      // Owner prefers in-app browser only — never a separate window.
      setInAppBrowser({ url: action.result.url, title: action.result?.title || "Link" });
      return;
    }
    if (
      (action.type === "open_youtube" || action.type === "open_my_trader" || action.type === "open_my_clothing" ||
       action.type === "open_stripe_dashboard" || action.type === "open_ring" || action.type === "open_gmail" ||
       action.type === "open_google_calendar" || action.type === "open_github" || action.type === "open_drive" ||
       action.type === "open_youtube_studio" || action.type === "open_snapdog_dashboard" || action.type === "open_rawcompany_hub" ||
       action.type === "open_snapdog_app" || action.type === "open_rawcompany_app" ||
       action.type === "open_clothing_dashboard" || action.type === "open_clothing_app" ||
       action.type === "open_trader_dashboard" || action.type === "open_trader_app" ||
       action.type === "open_racing_dashboard" || action.type === "open_racing_app" ||
       action.type === "open_rawmixes_dashboard" || action.type === "open_rawmixes_app" ||
       action.type === "open_rawco_trading_app" || action.type === "open_rawco_trading_dashboard")
      && action.result?.url
    ) {
      const titleMap = {
        open_youtube: "YouTube", open_my_trader: "Capital.com Trader",
        open_my_clothing: "Clothing App", open_stripe_dashboard: "Stripe Dashboard",
        open_ring: "Ring Doorbell", open_gmail: "Gmail",
        open_google_calendar: "Google Calendar", open_github: "GitHub",
        open_drive: "Google Drive", open_youtube_studio: "YouTube Studio",
        open_snapdog_dashboard: "SnapDog Dashboard",
        open_rawcompany_hub: "RAW-DATA Hub",
        open_snapdog_app: "SnapDog",
        open_rawcompany_app: "RAW-DATA",
        open_clothing_dashboard: "Clothing Dashboard",
        open_clothing_app: "Clothing",
        open_trader_dashboard: "Trader Dashboard",
        open_trader_app: "Trader",
        open_racing_dashboard: "Racing Form Guide Dashboard",
        open_racing_app: "Racing Form Guide",
        open_rawmixes_dashboard: "Raw Mixes Dashboard",
        open_rawmixes_app: "Raw Mixes",
        open_rawco_trading_app: "RAW-DATA Trading",
        open_rawco_trading_dashboard: "RAW-DATA Trading Dashboard",
      };
      const title = titleMap[action.type] || action.type;
      // Owner prefers in-app browser ONLY — never a separate window.
      // `new_tab` flag from backend is intentionally ignored here.
      setInAppBrowser({ url: action.result.url, title });
      return;
    }
    if (action.type === "ask_all_agents") {
      const q = action.result?.question || action.action?.question || "";
      setSwarmAutoMsg(q);
      setSwarmOpen(true);
      return;
    }
    if (action.type === "start_debate") {
      const topic = action.result?.topic || "";
      // Open SwarmChat and pass a special "debate" prefix that the panel
      // detects and auto-fires the 🎭 DEBATE button.
      setSwarmAutoMsg(`__DEBATE__:${topic}`);
      setSwarmOpen(true);
      return;
    }
    if (action.type === "start_conclave") {
      // 🤝 CONCLAVE — agents huddle sequentially, calling each other by
      // name. Owner Feb 2026: "can my agents talk to each other and
      // collaborate?" — this is the voice trigger for it.
      const topic = action.result?.question || action.result?.topic || "";
      setSwarmAutoMsg(`__CONCLAVE__:${topic}`);
      setSwarmOpen(true);
      return;
    }
    if (action.type === "pin_app_as_widget" && action.result?.ok) {
      // Backend already created the widget — tell the background to refresh
      window.dispatchEvent(new Event("rawco-widgets-changed"));
      return;
    }
    if (action.type === "play_radio" && action.result?.stream_url) {
      // Stop anything currently playing then start the new station
      try { jarvis.stopAudio?.(); } catch {}
      try { if (radioRef.current) { radioRef.current.pause(); radioRef.current.src = ""; } } catch {}
      try {
        const a = new Audio(action.result.stream_url);
        a.volume = 0.8;
        a.crossOrigin = "anonymous";
        a.play().catch((e) => console.warn("[radio] autoplay blocked:", e?.message));
        radioRef.current = a;
        setRadioPlaying({ station: action.result.station, name: action.result.name });
      } catch (e) { console.warn("[radio] failed:", e?.message); }
      showVisual({ type: action.type, action });
      return;
    }
    if (action.type === "stop_audio") {
      // Cancel TTS, the live radio stream, and any HTML audio elements on the page
      try { jarvis.stopAudio?.(); } catch {}
      try { if (radioRef.current) { radioRef.current.pause(); radioRef.current.src = ""; radioRef.current = null; } } catch {}
      try {
        document.querySelectorAll("audio").forEach((el) => { try { el.pause(); el.currentTime = 0; } catch {} });
      } catch {}
      setRadioPlaying(null);
      return;
    }
    if (action.type === "read_bbc_news" && action.result?.mode === "live" && action.result?.stream_url) {
      // Play BBC live audio in the background — speech and audio both safe
      try {
        const a = new Audio(action.result.stream_url);
        a.play().catch(() => {});
      } catch {}
      showVisual({ type: action.type, action });
      return;
    }
    if (action.type === "list_voices" && Array.isArray(action.result?.voices)) {
      showVisual({ type: "list_voices", action });
      return;
    }
    if (action.type === "set_voice") {
      showVisual({ type: "set_voice", action });
      return;
    }
    if (action.type === "ebay_search" && action.result?.search_url) {
      setInAppBrowser({ url: action.result.search_url, title: "eBay" });
    }
    if (VISUAL_ACTIONS.has(action.type)) {
      showVisual({ type: action.type, action, reply });
    }
  };

  const [audioEl, setAudioEl] = useState(null);

  const jarvis = useJarvis({
    onTranscript: () => { userInteractedRef.current = true; setSuggestedReplies([]); },
    onReply: (m) => {
      setLastReply(m.text || "");
      // Brain may attach `suggested_replies` to its reply (when ending in a question)
      if (Array.isArray(m.suggested_replies)) setSuggestedReplies(m.suggested_replies);
      else setSuggestedReplies([]);
    },
    onAction: handleAction,
    onAudioElement: setAudioEl,
  });

  // Keep local orb/lightning state in sync with the server-side theme on first load
  useEffect(() => {
    if (themeHook.theme?.orb_style && themeHook.theme.orb_style !== orbStyle) setOrbStyle(themeHook.theme.orb_style);
    if (themeHook.theme?.lightning_color && themeHook.theme.lightning_color !== lightningColor) setLightningColor(themeHook.theme.lightning_color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeHook.theme]);

  // 🎭 Listen for SwarmChat's per-agent 🔊 buttons — when the owner clicks
  // to hear one of the swarm responses, pop that agent's face out too.
  useEffect(() => {
    const onShow = (e) => {
      const d = (e && e.detail) || {};
      if (!d.name) return;
      clearTimeout(agentFaceClearTimerRef.current);
      setAgentFaceLeaving(false);
      setActiveAgentFace({ name: d.name, voiceId: d.voiceId || "" });
    };
    const onHide = () => {
      setAgentFaceLeaving(true);
      agentFaceClearTimerRef.current = setTimeout(() => {
        setActiveAgentFace(null);
        setAgentFaceLeaving(false);
      }, 480);
    };
    const onAudio = (e) => {
      // SwarmChat exposes the <audio> element so the face can lip-sync.
      if (e?.detail?.audio) setAudioEl(e.detail.audio);
    };
    window.addEventListener("rawco-agent-face-show", onShow);
    window.addEventListener("rawco-agent-face-hide", onHide);
    window.addEventListener("rawco-agent-face-audio", onAudio);
    return () => {
      window.removeEventListener("rawco-agent-face-show", onShow);
      window.removeEventListener("rawco-agent-face-hide", onHide);
      window.removeEventListener("rawco-agent-face-audio", onAudio);
    };
  }, []);

  // 🎭 AUDIO-SYNCED AGENT FACE — commit the pending agent face only when
  // audioEl flips to a NEW element (= TTS audio is ready to play). This
  // eliminates the 30-second silent face the owner reported. The
  // <audio>.play() call inside speak() resolves AS the audio begins, and
  // useJarvis fires onAudioElement(audio) right before that — so by the
  // time this effect runs, sound is starting.
  useEffect(() => {
    if (!audioEl) return undefined;
    if (!pendingAgentFaceRef.current) return undefined;
    const next = pendingAgentFaceRef.current;
    pendingAgentFaceRef.current = null;
    clearTimeout(agentFaceClearTimerRef.current);
    setAgentFaceLeaving(false);
    setActiveAgentFace(next);
    setPendingAgentFace(false);
  }, [audioEl]);

  // Clear the face shortly after speaking ends so the owner sees the orb again.
  useEffect(() => {
    if (jarvis.speaking) return undefined;
    // If speaking ended before audio ever committed the face, drop the
    // pending flag so we don't suppress the orb forever.
    if (pendingAgentFaceRef.current) {
      pendingAgentFaceRef.current = null;
      setPendingAgentFace(false);
    }
    if (!activeAgentFace) return undefined;
    setAgentFaceLeaving(true);
    agentFaceClearTimerRef.current = setTimeout(() => {
      setActiveAgentFace(null);
      setAgentFaceLeaving(false);
    }, 520);
    return () => clearTimeout(agentFaceClearTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jarvis.speaking, !!activeAgentFace]);

  // 🚨 PERF — pause heavy work when tab is in the background. Owner
  // complaint: "whole app is crashing and extremely slow sometimes".
  // Root cause was 8+ polling loops + 3D rendering + camera decode all
  // running concurrently. When the tab is hidden (mirror window in
  // background, or user switched apps), we set a global hidden flag that
  // every poll checks before doing work.
  const [docHidden, setDocHidden] = useState(typeof document !== "undefined" ? document.hidden : false);
  useEffect(() => {
    const onVis = () => setDocHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // 👁 PROACTIVE EYE WATCHER — OPT-IN ONLY. Owner complaint: "Sarah keeps
  // trying to talk back to me first when I didn't even say her name" —
  // any unsolicited voice (eye, nudges, etc.) collides with the main
  // brain and freezes the orb. We now require an explicit opt-in flag
  // (localStorage: rawco-eye-autospeak = "1") before the eye narrates
  // out loud. Default OFF.
  useEffect(() => {
    const enabled = (() => {
      try { return localStorage.getItem("rawco-eye-autospeak") === "1"; } catch { return false; }
    })();
    if (!enabled) return undefined;
    let stopped = false;
    const poll = async () => {
      if (stopped || docHidden) return;
      try {
        const r = await api.get("/jarvis/eye_announcement");
        if (stopped) return;
        const a = r.data?.announcement;
        if (a && !jarvis.speaking && !jarvis.listening && !jarvis.recording) {
          jarvis.speak?.(`Sir, I can see ${a.toLowerCase().startsWith("i ") ? a : a}`);
        }
      } catch { /* ignore */ }
    };
    const id = setInterval(poll, 30000);
    poll();
    return () => { stopped = true; clearInterval(id); };
  }, [jarvis, docHidden]);

  useEffect(() => { getStatus().then(setStatus).catch(() => {}); }, []);

  // 🎭 Agent face auto-clear — when jarvis.speaking drops to false while a
  // face is mounted, play the fade-out keyframes (.fade-out class) for
  // ~500ms then unmount. This sequencing makes the face glide off-screen
  // rather than vanishing instantly.
  useEffect(() => {
    if (!activeAgentFace) return;
    if (jarvis.speaking) {
      // Speaking again — make sure we're not in leaving state and clear
      // any stale unmount timer.
      clearTimeout(agentFaceClearTimerRef.current);
      if (agentFaceLeaving) setAgentFaceLeaving(false);
      return;
    }
    // Speaking just finished — start the leave animation
    setAgentFaceLeaving(true);
    agentFaceClearTimerRef.current = setTimeout(() => {
      setActiveAgentFace(null);
      setAgentFaceLeaving(false);
    }, 480);
    return () => clearTimeout(agentFaceClearTimerRef.current);
  }, [jarvis.speaking, activeAgentFace, agentFaceLeaving]);

  // Honour PWA shortcuts: visiting /?cmd=capital_pnl auto-dispatches that
  // action so the iOS / Android home-screen shortcuts land users straight on
  // the panel they wanted.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cmd = params.get("cmd");
    if (!cmd) return;
    const map = {
      capital_pnl: "show me my live capital P&L",
      morning_brief: "give me my morning brief",
      settings: "open settings",
      overnight: "what did you do overnight",
      hub: "open my RAW Company hub dashboard",
    };
    const phrase = map[cmd];
    if (phrase) {
      // Wait for auth/boot to settle then fire. Mark as user interaction so
      // the boot's auto-morning-brief doesn't clobber the deeplinked panel.
      userInteractedRef.current = true;
      setTimeout(() => jarvis.sendText?.(phrase), 1500);
      // Clean the URL so reload doesn't re-fire
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for queued spoken alerts (auto-scalper trade events). VISUAL-ONLY
  // by default. Owner complaint: "two agents talking over each other" was
  // partially caused by this loop firing multiple alerts back-to-back with
  // only 600ms between them — each speak() superseded the previous via
  // reqId dedup, so partial sentences from one alert + another overlapped.
  // The mute window (jarvis.speaking||jarvis.thinking) wasn't tight enough.
  // We now ONLY auto-speak alerts if the owner opts in via
  // localStorage.rawco-alerts-autospeak === "1". Default: silent (alerts
  // are still surfaced as the lastReply text + show up in chat).
  useEffect(() => {
    if (!localStorage.getItem("rawco_token")) return;
    const autospeak = (() => {
      try { return localStorage.getItem("rawco-alerts-autospeak") === "1"; } catch { return false; }
    })();
    let alive = true;
    const tick = async () => {
      try {
        if (jarvis.speaking || jarvis.thinking) return;
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/spoken_alerts/pending`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("rawco_token")}` },
        });
        if (!r.ok) return;
        const j = await r.json();
        for (const a of (j.alerts || [])) {
          if (!alive) return;
          // Show in the reply ticker no matter what — owner still sees it.
          setLastReply(a.text);
          if (!autospeak) continue;  // silent by default
          if (jarvis.speaking || jarvis.thinking) return;
          jarvis.speak?.(a.text);
          // Wait long enough that the next alert doesn't supersede this one.
          // 600ms was too tight; we now wait until speaking flips back off
          // (max 30s as a safety bound).
          const start = Date.now();
          while (jarvis.speaking && Date.now() - start < 30000) {
            await new Promise(res => setTimeout(res, 200));
          }
        }
      } catch {}
    };
    tick();
    const id = setInterval(tick, 12000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jarvis.speaking, jarvis.thinking]);

  // Galaxy icon-click bridge — clicking an orbiting integration star fires
  // its voice command. Mounted AFTER useJarvis() so the ref is defined.
  useEffect(() => {
    const onIconClick = (e) => {
      userInteractedRef.current = true;
      jarvis.sendText?.(e.detail?.cmd || "");
    };
    window.addEventListener("rawco-icon-click", onIconClick);
    return () => window.removeEventListener("rawco-icon-click", onIconClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jarvis.sendText]);

  // 🚀 SWARM-ACTION BRIDGE — owner complaint: "the agents say they're doing
  // things but nothing actually opens on the mirror". SwarmChat now fires
  // a `rawco-swarm-action-execute` event when the backend auto-executes
  // a whitelisted action. We feed it straight through `handleAction` so
  // it travels the same pipeline as the main brain's actions: showVisual
  // → BroadcastChannel → mirror render. This is what makes the card
  // actually appear on the second screen.
  useEffect(() => {
    const onSwarmAction = (e) => {
      const act = e.detail?.action;
      const reply = e.detail?.reply || "";
      if (act && act.type) {
        try { handleAction(act, reply); } catch (err) { console.warn("[swarm-action] handler failed", err); }
      }
    };
    window.addEventListener("rawco-swarm-action-execute", onSwarmAction);
    return () => window.removeEventListener("rawco-swarm-action-execute", onSwarmAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reusable boot sequence — used both by first-of-day startup AND by the
  // idle-screensaver "mouse wake" so the wake-up feels cinematic every time.
  // `auto_brief` controls whether to fire the morning brief 8s after greeting
  // (we skip it on screensaver-wake so we don't bombard the owner mid-day).
  const runBootSequence = useCallback(async ({ greetOverride = null, autoBrief = true } = {}) => {
    // ⚡ Owner Feb 2026: brief not firing on iOS Safari. Root cause: the
    // outer try/catch wrapped the WHOLE function — when playBootStinger
    // threw (autoplay blocked by mobile browsers) the boot sequence
    // aborted before the brief setTimeout was even scheduled. Now each
    // pre-step is wrapped in its own try/catch so an audio failure can
    // never kill the brief.
    try { await playWakeSongIfUploaded() || await playBootStinger(); } catch (e) { console.warn("[boot] audio failed (autoplay block?):", e?.message); }
    try {
      const greet = greetOverride || greetingForHour();
      setLastReply(greet);
      try { jarvis.speak(greet); } catch (e) { console.warn("[boot] speak failed:", e?.message); }
    } catch (e) { console.warn("[boot] greeting failed:", e?.message); }
    if (autoBrief) {
      // Brief is scheduled OUTSIDE the try/catch chain above so nothing
      // upstream can suppress it.
      setTimeout(() => {
        // 30s dedup catches React strict-mode double-mount and rapid
        // refreshes. Every other guard (visualRef, userInteracted,
        // jarvis.speaking, fresh-sign-in check) was deleted Feb 2026 —
        // owner contract: "the brief should be automatically displayed
        // every time the app is opened", full stop.
        // 🛡 Use a LOCAL ref instead of window.__rcoLastBriefAt — the
        // global is the 5-minute lock owned by useJarvis.sendToBrain.
        // Setting it here before sendText fired the lock pre-emptively
        // and the brief never played ("brief lock active" in console).
        const sinceLast = Date.now() - Number(autoBriefLastAtRef.current || 0);
        if (sinceLast < 30000) return;
        autoBriefLastAtRef.current = Date.now();
        try { sessionStorage.removeItem("rawco_just_signed_in"); } catch { /* ignore */ }
        try { jarvis.sendText?.("give me my morning brief", "morning_brief", { silentOnError: true }); } catch (e) { console.warn("[boot] brief send failed:", e?.message); }
      }, 8000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-load-of-the-day boot sequence: uploaded wake song (or synth stinger
  // fallback) → time-of-day greeting → AUTO-FIRE morning brief panel so the
  // owner sees real metrics without pressing anything.
  useEffect(() => {
    // ⚡ Owner Feb 2026: brief not firing on login.
    // Fresh sign-ins ALWAYS fire the brief, even if the boot key is set
    // from an earlier session in the same tab. Tabs can persist
    // sessionStorage across logout/login cycles so without this override
    // the brief silently skips on every login after the first one.
    const justSignedInAt = Number(sessionStorage.getItem("rawco_just_signed_in") || 0);
    const isFreshSignIn = justSignedInAt > 0 && (Date.now() - justSignedInAt) < 30000;
    if (!isFreshSignIn && !shouldFireBoot()) return;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      markBootFired();
      await runBootSequence({ autoBrief: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open mirror on first mount:
  //   • if owner previously opted-in (`rawco_mirror_pref === "on"`)  → open
  //   • OR if browser reports more than one physical display via the
  //     Window Management API → open. This makes the second-screen pop
  //     automatic on the owner's setup (laptop + external monitor) without
  //     them having to tap the button next to the orb every boot.
  //   Will silently no-op on single-screen devices.
  useEffect(() => {
    const pref = localStorage.getItem("rawco_mirror_pref");
    if (pref === "off") return; // explicit owner opt-out wins
    let cancelled = false;
    const tryAuto = async () => {
      if (pref === "on") return popOutMirror();
      // Multi-screen detection (Chrome/Edge desktop only). Permission is
      // granted-on-first-use after the user clicks "Allow" on the browser
      // prompt; until then `getScreenDetails` rejects and we silently bail.
      if (!window.getScreenDetails) return;
      try {
        const sd = await window.getScreenDetails();
        if (cancelled) return;
        if ((sd.screens || []).length > 1) {
          // Found multiple monitors — auto-open AND remember the pref so
          // subsequent boots don't have to ask again.
          localStorage.setItem("rawco_mirror_pref", "on");
          popOutMirror();
        }
      } catch {
        // Permission not granted yet — owner will tap the button once and
        // we'll remember it for next time.
      }
    };
    const t = setTimeout(tryAuto, 600);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // 🚨 PERF — was 1000ms which forced an App-wide re-render every second
    // (state lifted to <App>, so the entire orb screen + every memoised
    // child got compared). 30s is enough for any time-of-day text on the
    // root component; the dedicated 1-second clocks in TopBar / Display /
    // BackgroundWidgets are self-scoped so they don't cascade.
    const t = setInterval(() => setTime(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // AUTO-UNLOCK audio on the very first user gesture anywhere on the page.
  // We need ONE user gesture so the browser allows audio playback later, but
  // we MUST NOT fire jarvis.activate() (which speaks a greeting) — that was
  // making EVERY button click speak "right here sir", drowning every other
  // reply. We just play a silent 1-frame mp3 to unlock autoplay, nothing else.
  const audioUnlockedRef = useRef(false);
  useEffect(() => {
    const unlock = () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      try {
        const a = new Audio("data:audio/mpeg;base64,SUQzAwAAAAAAFlRTU0UAAAAMAAADTGF2ZjU3LjcxLjEwMP/7kGQAAAAAAAAAAAAAAAAAAAAAAAAA");
        a.volume = 0;
        a.play().catch(() => {});
      } catch {}
      window.removeEventListener("click", unlock);
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("touchstart", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Notification permission request
  const lastSeenTaskRef = useRef(null);
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Widget click → open full app in InAppBrowser. Listens for the event the
  // iframe widget overlay fires (BackgroundWidgets.jsx -> openFullApp).
  useEffect(() => {
    const onOpen = (e) => {
      const { url, title } = e.detail || {};
      if (url) setInAppBrowser({ url, title: title || "Dashboard" });
    };
    window.addEventListener("rawco-open-in-browser", onOpen);
    return () => window.removeEventListener("rawco-open-in-browser", onOpen);
  }, []);

  // Mini-orb tap → same behaviour as tapping the big orb (press to talk).
  // Owner asked: "can I have the orb as a pop up little widget I can move
  // around that behaves the same way as the big un-moveable one".
  useEffect(() => {
    if (!jarvis?.pressToTalk) return;
    const onMiniTap = () => { try { jarvis.pressToTalk(); } catch {} };
    window.addEventListener("rawco-orb-tap", onMiniTap);
    return () => window.removeEventListener("rawco-orb-tap", onMiniTap);
  }, [jarvis?.pressToTalk]);

  // ─── SYSTEM-MAP CLICKS → OPEN AGENT STUDIO / SETTINGS ──────────────────
  // The architecture diagram (StageVisual show_architecture) dispatches
  // these events when the owner taps a cluster. Owner asked: "make the
  // diagram clickable so I can edit each agent's voice/persona/prompt
  // all in one place".
  useEffect(() => {
    const onAgent = (e) => {
      // Owner complaint: clicking ✎/🎙 on an agent row was firing
      // "Open Tom's settings" INTO swarm chat → all agents replied.
      // Fix: open AgentStudio DIRECTLY via the visual pipeline, no
      // chat broadcast. The `agent` + `voiceStage` detail is passed
      // through so AgentStudio can pre-select & scroll to the voice.
      const agent = e.detail?.agent;
      const voiceStage = !!e.detail?.voiceStage;
      showVisual({
        type: "agent_studio",
        action: { type: "agent_studio", agent, voiceStage },
      });
    };
    const onSettings = (e) => {
      const tab = e.detail?.tab || "general";
      // Route via the visual pipeline → SettingsPanel reads `initialTab`.
      showVisual({ type: "settings", action: { type: "settings", tab } });
    };
    window.addEventListener("rawco-open-agent-studio", onAgent);
    window.addEventListener("rawco-open-settings", onSettings);
    return () => {
      window.removeEventListener("rawco-open-agent-studio", onAgent);
      window.removeEventListener("rawco-open-settings", onSettings);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Polls /api/jarvis/pending_actions every 8s. When the owner walks in and
  // says "Hey Siri, run Good Morning RAW-DATA" (or the ESP32 hears the
  // wake phrase), the webhook sets `pending_morning_brief = true`. This
  // effect picks it up and fires the brief automatically — no taps, no
  // clicks, just walk in and the brief plays through the desk speakers.
  // ALSO picks up `pending_alarm` when an alarm fires — overlays the
  // AlarmRinger with the ringing tone and the wake greeting.
  const [alarmActive, setAlarmActive] = useState(false);
  const [alarmLabel, setAlarmLabel] = useState("");
  const [alarmHhmm, setAlarmHhmm] = useState("");
  useEffect(() => {
    if (!jarvis?.sendText) return;
    let stopped = false;
    let lastFiredAt = 0;
    // 🛡 Cross-instance brief-firing guard — owner saw "two briefs back-to-back
    // reading half the cards each". Two triggers can race: boot autoBrief
    // (8s post-greeting) + pending_morning_brief poller. Lift the cooldown
    // from 60s to 5 minutes AND share the lock with the boot path via a
    // window-level timestamp.
    const tick = async () => {
      if (stopped) return;
      try {
        const r = await api.get("/jarvis/pending_actions");
        if (r.data?.pending_alarm) {
          setAlarmLabel(r.data.alarm_label || "Wake up");
          setAlarmHhmm(r.data.alarm_hhmm || "");
          setAlarmActive(true);
        }
        if (r.data?.pending_morning_brief) {
          const lastGlobalBriefAt = Number(window.__rcoLastBriefAt || 0);
          const lastLocalBriefAt = lastFiredAt;
          const sinceLastBrief = Date.now() - Math.max(lastGlobalBriefAt, lastLocalBriefAt);
          if (sinceLastBrief > 300000 && !jarvis.thinking && !jarvis.speaking) {
            lastFiredAt = Date.now();
            // 🛡 Do NOT pre-set window.__rcoLastBriefAt — the hook
            // sets it itself after passing its own lock check. Pre-
            // setting from here makes the hook see a fresh lock and
            // silently abort the very brief we just queued.
            jarvis.sendText("give me my morning brief", "morning_brief", { silentOnError: true });
          }
        }
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => { stopped = true; clearInterval(id); };
  }, [jarvis?.sendText]);
  useEffect(() => {
    const poll = async () => {
      try {
        const tk = localStorage.getItem("rawco_token");
        if (!tk) return;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks?hours=1`, { headers: { Authorization: `Bearer ${tk}` } });
        if (!res.ok) return;
        const r = await res.json();
        if (!Array.isArray(r) || !r.length) return;
        const latest = r[0];
        if (lastSeenTaskRef.current === null) { lastSeenTaskRef.current = latest.id; return; }
        if (latest.id !== lastSeenTaskRef.current) {
          const idx = r.findIndex(t => t.id === lastSeenTaskRef.current);
          const newOnes = idx === -1 ? r.slice(0, 5) : r.slice(0, idx);
          for (const t of newOnes) {
            const fire = t.title.startsWith("⏰") || t.title.startsWith("👁");
            if (fire && typeof Notification !== "undefined" && Notification.permission === "granted") {
              new Notification("RAW-DATA", { body: t.title, icon: "/raw-data-logo-192.png", tag: t.id });
            }
          }
          lastSeenTaskRef.current = latest.id;
        }
      } catch {}
    };
    const id = setInterval(poll, 8000);
    return () => clearInterval(id);
  }, []);

  const onLogout = () => { logout(); window.dispatchEvent(new Event("rawco-logout")); };
  const orbActive = jarvis.listening || jarvis.speaking || jarvis.thinking;

  // 🎚 USB mic mute-button → trigger morning brief.
  // DISABLED: the browser's SpeechRecognition (always-on listening) toggles
  // the audio track's mute state constantly, which made this listener fire
  // in a loop → infinite morning-brief calls → "SPEAKING" stuck on, frozen
  // voice wave / background. Removed until a more reliable signal source
  // (e.g. WebHID with the exact Airhub21 vendor/product IDs) is available.
  // Voice command "give me my morning brief" still works.

  const timeStr = now.toTimeString().slice(0, 8);
  const dateStr = now.toDateString().toUpperCase();

  return (
    <div className="App min-h-screen relative overflow-hidden" data-testid="console-page">
      {/* Mirror-open prompt — small pill in the top-left under the clock.
          ONLY shown after the user has actually triggered a brief / panel
          that needs the mirror — never on initial app load. Owner spent
          dozens of credits trying to stop this from appearing on start;
          we now require an explicit user request before this surfaces. */}
      {mirrorPromptVisible && !mirrorOpen && pendingVisualsRef.current?.length > 0 && (
        <button
          type="button"
          onClick={() => { popOutMirror(); setMirrorPromptVisible(false); }}
          data-testid="mirror-open-prompt-btn"
          style={{
            position: "fixed", top: 80, left: 16, zIndex: 95,
            padding: "6px 12px",
            background: "rgba(0,4,12,0.92)",
            border: "1px solid var(--rc-gold)",
            color: "var(--rc-gold)",
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 10, letterSpacing: 1.5, cursor: "pointer",
            borderRadius: 999,
            boxShadow: "0 0 12px rgba(255,184,0,0.4)",
          }}
        >
          📺 OPEN MIRROR
        </button>
      )}
      <ThemedBackground background={themeHook?.theme?.background} />
      <StageEffects color={lightningColor} trigger={lightningTrigger} targetRect={panelRect} />
      <StatusBar onOpenPanel={(action) => showVisual({ type: action.type, action })} />
      {/* HARD CONTRACT (owner-imposed, asked many times):
          Orb screen = ORB + agent chat + mini orb ONLY. No background widgets.
          All widgets live on the /display mirror screen. */}
      {/* <BackgroundWidgets /> */}
      <MiniOrbWidget active={jarvis.listening || jarvis.recording} speaking={jarvis.speaking} />
      {/* Voice spectrum widget removed per owner request — was getting in the way. */}

      {/* Centre orb (or CRT-head face when orb style is "crt_head") —
          always visible. Tap = press-to-talk (record + Whisper). */}
      <div className="fixed inset-0 z-[2] flex items-center justify-center pointer-events-none">
        <div className="relative w-[min(70vw,560px)] h-[min(70vw,560px)] pointer-events-auto">
          {orbStyle === "crt_head" ? (
            <TalkingHead
              active={orbActive}
              listening={jarvis.listening || jarvis.recording}
              speaking={jarvis.speaking}
              audioEl={audioEl}
              onTap={jarvis.pressToTalk}
              agentName={activeAgentFace?.name || "RAW-DATA"}
            />
          ) : (
            <>
              <JarvisOrb active={orbActive} listening={jarvis.listening || jarvis.recording} style={orbStyle} />
              {jarvis.speaking && <OrbWaveform audioEl={audioEl} active={jarvis.speaking} />}
              <button
                onClick={() => {
                  // 🛑 Owner Feb 2026: "orb is just spinning, says speaking
                  // but isn't, stuck in a loop". Tap-to-interrupt — if the
                  // orb is currently showing SPEAKING, the first tap KILLS
                  // the audio and clears the stuck flag instantly.
                  // Otherwise it does the normal press-to-talk.
                  if (jarvis.speaking) {
                    try { jarvis.stopAudio?.(); } catch { /* ignore */ }
                    return;
                  }
                  jarvis.pressToTalk();
                }}
                className={`absolute inset-0 cursor-pointer rounded-full ${jarvis.recording ? "ring-4 ring-red-500/60 animate-pulse" : ""}`}
                aria-label={jarvis.speaking ? "Tap to interrupt" : jarvis.recording ? "Tap to stop recording" : "Tap to speak"}
                data-testid="orb-tap"
                title={jarvis.speaking ? "Tap to interrupt speech" : jarvis.recording ? "Recording… tap again to send" : "Tap once to talk, tap again to send"}
              />
            </>
          )}
          {/* 🔇 MUTE pill — owner asked for a button around the orb to
              silence the mic without having to fish through settings. Sits
              top-right of the orb so it never covers the centre. Clicking
              toggles jarvis.muted (state persisted in localStorage). */}
          <button
            type="button"
            onClick={() => jarvis.setMuted?.(!jarvis.muted)}
            data-testid="orb-mute-toggle"
            title={jarvis.muted ? "Tap to UN-MUTE the mic" : "Tap to MUTE the mic"}
            className="absolute z-[3] rounded-full text-[10px] tracking-widest"
            style={{
              top: "8%", right: "8%",
              width: 56, height: 56,
              border: `1.5px solid ${jarvis.muted ? "#ff4d6d" : "var(--rc-gold)"}`,
              background: jarvis.muted ? "rgba(255,77,109,0.18)" : "rgba(0,4,12,0.7)",
              color: jarvis.muted ? "#ff4d6d" : "var(--rc-gold)",
              boxShadow: jarvis.muted
                ? "0 0 18px rgba(255,77,109,0.55), inset 0 0 8px rgba(255,77,109,0.25)"
                : "0 0 12px rgba(255,184,0,0.35), inset 0 0 6px rgba(255,184,0,0.18)",
              fontFamily: "'JetBrains Mono', monospace",
              cursor: "pointer",
              transition: "all 220ms ease-out",
            }}
          >
            {jarvis.muted ? "MUTED" : "MUTE"}
          </button>
        </div>
      </div>

      {/* 🎭 Per-agent 3D talking face — pops out when a specific agent
          (Tom/Sarah/Eva/Scout/Dobby) is the one talking. Lip-syncs to
          their voice via Web Audio analyser, fades out when they're done.
          When the MAIN brain (JARVIS) is the one speaking — i.e. no
          activeAgentFace is set — we show the MainRobotHead (TV-head
          circuit-board robot) inside the orb centre instead, per the
          owner's screenshot 4 reference. */}
      <AgentFaceStage
        agent={activeAgentFace}
        speaking={jarvis.speaking}
        audioEl={audioEl}
        leaving={agentFaceLeaving}
      />
      {!activeAgentFace && !pendingAgentFace && jarvis.speaking && (
        <div className="fixed inset-0 z-[8] flex items-center justify-center pointer-events-none" data-testid="main-robot-stage">
          <MainRobotHead audioEl={audioEl} speaking={jarvis.speaking} size={220} />
        </div>
      )}

      {/* ⏻ "Good Night RAW-DATA" full-screen farewell — visible
          30-second countdown with spoken farewell + screen dim. Triggered
          by the Siri shortcut → hardware_sleep webhook. The Windows
          helper script still does the actual shutdown 45s after the flag;
          we just make the wind-down VISIBLE to the owner. */}
      <ShutdownOverlay visible={showShutdown} />

      {/* Floating "second screen" pop-out button — sits to the right of the orb.
          Replaces the old auto-popout (which was browser-blocked on production).
          A direct click is a user gesture so popups go through reliably. */}
      <button
        type="button"
        onClick={() => popOutMirror()}
        data-testid="second-screen-btn"
        title={mirrorOpen ? "Mirror is on — click to refocus" : "Pop the second-screen mirror open"}
        className="fixed z-[15] pointer-events-auto"
        style={{
          // Sit to the right of the orb: orb's halo is ~min(70vw,560px)/2 ≈ 280px
          // wide. Add a 56px gap. On narrow viewports (<900px) the orb shrinks
          // proportionally so the calc still anchors cleanly to the right edge
          // of the orb.
          right: "calc(50% - min(35vw, 280px) - 110px)",
          top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(0,0,0,0.65)",
          border: `1px solid ${mirrorOpen ? "var(--rc-gold)" : "var(--rc-gold-dim)"}`,
          color: "var(--rc-gold)",
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: "10px",
          letterSpacing: "0.16em",
          padding: "10px 12px",
          backdropFilter: "blur(4px)",
          boxShadow: mirrorOpen ? "0 0 18px rgba(255,184,0,0.4)" : "0 0 10px rgba(255,184,0,0.2)",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        <div style={{ fontSize: 18, marginBottom: 4, lineHeight: 1 }}>🖥</div>
        <div>{mirrorOpen ? "MIRROR ON" : "2ND SCREEN"}</div>
      </button>


      {/* Click-to-fire command rail on the left (substitute for mic until USB mic arrives) */}
      <CommandRail
        onReply={(r) => {
          if (!r) return;
          if (r.reply) {
            setLastReply(r.reply);
            jarvis.speak(r.reply);
          }
          if (r.action) handleAction(r.action);
        }}
      />

      {/* Minimal top status */}
      <div className="stage-status">
        <span className="rc-label text-[10px]">
          {jarvis.thinking ? "THINKING" : jarvis.speaking ? "SPEAKING" : jarvis.listening ? "LISTENING" : jarvis.wakeArmed ? "STANDBY" : "MUTED"}
        </span>
        <span className={`w-2 h-2 ${orbActive ? "bg-[var(--rc-gold)] rc-pulse-glow" : "bg-[var(--rc-gold-dim)]"}`} />
        <div className="ml-3 text-right">
          <div className="font-display rc-glow-text text-sm tabular-nums">{timeStr}</div>
          <div className="rc-label text-[9px]">{dateStr}</div>
        </div>
      </div>

      {/* Status — read-only. Always-on mic. Mic-blocked panel walks the user through unblocking. */}
      {jarvis.micStatus === "denied" && (
        <MicBlockedPanel />
      )}

      {/* StageVisual now renders on the orb ONLY when the mirror isn't
          available. Owner complaint: "show me the system map / Ferrari /
          architecture — NOTHING shows up". Cause: cards were exclusively
          pushed to the mirror window, but if it wasn't open the owner
          saw nothing. Now we always have a fallback render on the orb. */}
      {!mirrorOpen && (
        <div className="stage-visual fixed inset-0 z-[60] pointer-events-none flex items-center justify-center"
             data-testid="orb-stage-visual">
          <div className="pointer-events-auto">
            <StageVisual
              key={visualKey}
              visual={visual}
              dismiss={() => setVisual(null)}
              onSend={(text, hint) => jarvis.sendText?.(text, hint)}
              /* ⚡ CLARIFY RELIABILITY — owner reported Feb 2026: "I can't
                 close the clarify card on the live app". Root cause: the
                 orb's StageVisual was mounted with only an `onClose` prop,
                 but the component expects `dismiss`, `onChoice`, `onSend`
                 (same contract Display.jsx uses on the mirror). So the X
                 button, the Esc key, the 120s auto-dismiss, and the
                 option buttons all silently no-op'd. Wired everything
                 through so the orb clarify behaves identically to the
                 mirror. */
              onChoice={(opt) => {
                try { jarvis.sendText?.(opt); } catch { /* ignore */ }
                setVisual(null);
              }}
            />
          </div>
        </div>
      )}
      {/* Pinned (docked) panels — auto-fit tiled grid on the right ~55% of
          the viewport.  Each tile can be dragged to rearrange, minimised, or
          closed.  See .pinned-dock CSS-grid in App.css and PinnedTile.jsx. */}
      {/* HARD CONTRACT: pinned-dock disabled on the orb — never render cards
          on this screen. Pinning happens only on the /display mirror. */}
      {/* {pinned.length > 0 && ... } */}
      {/* Follow-up reply chips — appear above the command bar when the brain
          ends with a question. Tap one to answer hands-free. */}
      <FollowUpChips
        options={suggestedReplies}
        onPick={(opt) => {
          userInteractedRef.current = true;
          setSuggestedReplies([]);
          jarvis.sendText?.(opt);
        }}
        onDismiss={() => setSuggestedReplies([])}
      />

      {/* Live UK radio "ON AIR" badge — top-right, click × to silence */}
      <RadioBadge
        station={radioPlaying}
        onStop={() => handleAction({ type: "stop_audio", result: { stopped: true } })}
      />

      {/* Idle screensaver — fades to black after 1hr of no input, then on
          mousemove resumes the boot song + greeting flow. */}
      <IdleScreensaver
        enabled={!!localStorage.getItem("rawco_token")}
        onWake={() => {
          userInteractedRef.current = false; // wake-up triggers the full cinema
          runBootSequence({ autoBrief: false });
        }}
      />

      {/* HARD CONTRACT: ProofCanvas is mounted ONLY on the /display mirror.
          The orb screen no longer renders proof cards under any circumstance. */}
      {/* <ProofCanvas mode="main" /> */}

      {/* Universal Command Bar — type any natural-language instruction. */}
      <UniversalCommandBar
        onReply={(m) => {
          if (m.role === "jarvis") {
            setLastReply(m.text);
            if (Array.isArray(m.suggested_replies)) setSuggestedReplies(m.suggested_replies);
            else setSuggestedReplies([]);
          } else if (m.role === "owner") {
            // Owner just sent a new message — clear stale chips + flag interaction
            userInteractedRef.current = true;
            setSuggestedReplies([]);
          }
        }}
        onAction={handleAction}
        speak={(t) => jarvis.speak?.(t)}
      />
      {/* Quick Actions floating button — 6-action radial menu, mobile-friendly. */}
      <QuickActionsFAB onPhrase={(p, hint) => { userInteractedRef.current = true; jarvis.sendText?.(p, hint); }} />
      <QuickWidgetLauncher />
      <AgentRemindersToast />
      <LayoutTemplatesModal open={layoutsOpen} onClose={() => setLayoutsOpen(false)} />
      <button
        onClick={() => setLayoutsOpen(true)}
        className="fixed top-4 right-32 z-[101] border border-[var(--rc-gold-dim)] text-[var(--rc-gold)] px-3 py-1.5 text-[10px] tracking-widest opacity-70 hover:opacity-100 hover:border-[var(--rc-gold)] bg-[rgba(0,4,12,0.6)] backdrop-blur"
        data-testid="open-layouts-button"
        title="Save/Load mirror layout templates"
      >
        📌 LAYOUTS
      </button>
      {/* Owner asked to remove the floating "💬 agent message" button — the
          AgentsRoster (click-to-talk + ✎ edit) and clicking agent nodes on
          the system map are now the only ways into SwarmChat / AgentStudio.
          Voice commands ("ask all my agents…") still work. */}
      {/* <SwarmButton onClick={() => { setSwarmAutoMsg(null); setSwarmOpen(true); }} /> */}
      {/* 👁 EYES — Phase 1: webcam-based vision, Phase 2: ESP32-S3 cam.
          Lets owner point a camera at anything and have agents see + narrate. */}
      <EyesButton onSpeak={(t) => jarvis.speak?.(t)} />

      {/* ⏰ ALARMS — floating button below EYES for the wake-up clock */}
      <AlarmsButton />

      {/* 📈 PAGE LINKS — drag-able pills for Markets + Spend + History */}
      <PageLinks />

      {/* 🔵 TRADING ORB — glowing blue button → /trading (drag-able) */}
      <TradingOrbButton />

      {/* 🟣 MIND COACH ORB — glowing violet button → /coach (drag-able).
          Owner Feb 2026: "I need this building in to my app" — 7 preset
          cognitive coach personas backed by Claude Sonnet 4.5. */}
      <CoachOrbButton />

      {/* 📮 AGENT INBOX — draggable notification bubble. Owner Feb 2026:
          "a little message box... I can accept / reply / say yes fix it".
          Notifies on email webhook + app-improvement scanner findings. */}
      <AgentInbox />

      {/* 🟢 ARM LIVE TRADING — big drag-able button to enable live Capital.com auto-trader */}
      <ArmLiveTradingButton />

      {/* 🤖 AUTO-TRADER voice alerts poller (invisible) */}
      <VoiceAnnouncementsPoller speak={jarvis.speak} />

      {/* 🤖 AGENTS ROSTER — visual list of all sub-agents under SwarmChat.
          Owner asked: "visual list of each agent with a little caption,
          clickable and editable, in little boxes underneath the swarm chat." */}
      <AgentsRoster
        onTalkTo={(a) => {
          const phrase = (a.nicknames || [])[0] || a.name;
          jarvis.sendText?.(`hey ${phrase}, what's on?`);
        }}
        onEdit={(a) => {
          // Open Agent Studio jumped to the agent — useful for editing
          // persona / guardrails / model.
          window.dispatchEvent(new CustomEvent("rawco-open-agent-studio", { detail: { agent: a.name } }));
        }}
        onEditVoice={(a) => {
          // 🎙 Owner brief (Feb 2026): "no where on the settings page has
          // any button or anything to change each voices?" — open Settings
          // directly on the new AGENT VOICES tab where every agent has a
          // preset dropdown, custom voice_id input, preview + save.
          window.dispatchEvent(new CustomEvent("rawco-open-settings", { detail: { tab: "voices", agent: a.name } }));
        }}
      />

      {/* ⏰ Alarm overlay — pops over everything when an alarm fires. Plays a
          looping tone via WebAudio, speaks the wake greeting via TTS, and
          gives STOP / SNOOZE buttons. */}
      <AlarmRinger
        active={alarmActive}
        label={alarmLabel}
        hhmm={alarmHhmm}
        onSpeak={(t) => jarvis.speak?.(t)}
        onStop={() => {
          setAlarmActive(false);
          // Clear server flag so it doesn't re-fire on next poll
          api.post("/jarvis/pending_actions/clear_alarm", {}).catch(() => {});
          // Owner asked: "make sure the morning brief starts when I've
          // turned my alarm off, not as soon as my alarm starts ringing —
          // I won't be at my screens by that point." So we kick off the
          // brief here, AFTER they've confirmed they're at the screen.
          // Honour the global brief lock so this can't race the
          // pending_morning_brief poller into a double-fire.
          setTimeout(() => {
            try {
              const sinceLast = Date.now() - Number(window.__rcoLastBriefAt || 0);
              if (sinceLast < 300000) return;
              // 🛡 Do NOT pre-set window.__rcoLastBriefAt — let
              // useJarvis.sendToBrain set it itself once it commits to
              // running the brief. Pre-setting from here would make
              // the hook abort with "brief lock active".
              jarvis.sendText?.("give me my morning brief", "morning_brief");
            } catch { /* ignore */ }
          }, 600); // small gap so the overlay's fade-out doesn't clash
        }}
        onSnooze={async () => {
          setAlarmActive(false);
          try {
            await api.post("/jarvis/pending_actions/clear_alarm", {});
            // Create a one-time alarm 9 min from now
            const t = new Date(Date.now() + 9 * 60000);
            const hh = String(t.getHours()).padStart(2, "0");
            const mm = String(t.getMinutes()).padStart(2, "0");
            const tz_offset_hours = -new Date().getTimezoneOffset() / 60;
            await api.post("/jarvis/alarms", {
              hhmm: `${hh}:${mm}`,
              label: alarmLabel ? `${alarmLabel} (snoozed)` : "Snoozed",
              repeat: "once",
              tz_offset_hours,
            });
          } catch { /* ignore */ }
        }}
      />
      {/* Owner asked to remove the floating orb chat widget — the main swarm
          chat at bottom-right (opened by SwarmButton) is the only one now. */}
      {/* <OrbChatWidget /> */}
      <SwarmChat
        open={swarmOpen}
        onClose={() => { setSwarmOpen(false); setSwarmAutoMsg(null); }}
        autoMessage={swarmAutoMsg}
      />
      <DoorbellPopup />
      {inAppBrowser && (
        <InAppBrowser
          url={inAppBrowser.url}
          title={inAppBrowser.title}
          onClose={() => setInAppBrowser(null)}
        />
      )}

      {/* Wake-word hint — kept LOW z-index so widgets stack on top, but
          NO LONGER gated on `!visual` so it stays visible even when the
          SwarmChat side-panel is open. Owner: "you took the RAW-DATA
          letters off the background". */}
      {!orbActive && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[1] text-center pointer-events-none flex flex-col items-center">
          <img
            src="/raw-data-logo.png"
            alt=""
            width={44}
            height={44}
            data-testid="stage-brand-logo"
            style={{ display: "block", marginBottom: 8, borderRadius: "50%", opacity: 0.95 }}
          />
          <div className="font-display text-2xl rc-glow-text tracking-widest">RAW-DATA</div>
          <div className="rc-label text-[9px] mt-1 opacity-60">
            {jarvis.recording ? "● RECORDING · TAP THE ORB TO SEND" : "TAP THE ORB TO SPEAK · OR SAY \"RAW COMPANY\""}
          </div>
        </div>
      )}

      {/* No on-screen subtitle — owner asked for it removed so it doesn't block the orb. Voice is the channel. */}
    </div>
  );
}

function SwarmButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      title="Open Swarm Chat — ask all my agents at once"
      data-testid="swarm-chat-trigger"
      className="fixed bottom-4 right-36 z-[101] w-12 h-12 rounded-full flex items-center justify-center font-display transition-all hover:scale-110"
      style={{
        background: "rgba(0,4,12,0.94)", border: "1px solid var(--rc-gold)",
        color: "var(--rc-gold)", boxShadow: "0 0 18px rgba(255,184,0,0.35)",
      }}
    >
      <span className="text-xl">💬</span>
    </button>
  );
}


export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/drop" element={<Drop />} />
        <Route path="/drop/success" element={<Drop />} />
        <Route path="/display" element={<Display />} />
        <Route path="/trading" element={<Trading />} />
        <Route path="/coach" element={<Coach />} />
        <Route path="/spend" element={<SpendTracker />} />
        <Route path="/history" element={<ChatHistory />} />
        <Route path="/showcard" element={<Showcard />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/payment/success" element={<Showcard />} />
        <Route path="/payment/cancel" element={<Showcard />} />
        <Route path="*" element={<Console />} />
      </Routes>
    </BrowserRouter>
  );
}
